import { VueEditor } from 'vue2-editor';

export default {
    components: {
        VueEditor
    },

    data() {
        return {
            activeTab: 'notice',
            currentNotice: {
                title: '',
                subject: '',
                body: ''
            },
            customToolbar: [
                ['bold', 'italic', 'underline', 'link'],
                [{ list: 'ordered' }, { list: 'bullet' }]
            ],
            savedNotices: [],
            emailHistory: [],
            selectedEmail: {},
            noticeToDelete: null,
            loading: false,
            currentAction: '',
            isEditing: false,
            editingId: null
        };
    },
    
    computed: {
        isFormValid() {
            return this.currentNotice.title.trim() !== '' && 
                   this.currentNotice.subject.trim() !== '' && 
                   this.stripHtml(this.currentNotice.body) !== '';
        }
    },
    
    async mounted() {
        await this.fetchNoticesQuiet();
    },

    methods: {
        async fetchNotices() {
            try {
                const response = await this.$lionheart.get('/v2/admin/email-notices');
                
                if (response.data.success) {
                    this.savedNotices = response.data.data.drafts;
                    this.emailHistory = response.data.data.history;
                } else {
                    this.$bvToast.toast('Failed to fetch notices', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                    });
                }
            } catch (error) {
                console.error('Error fetching notices:', error);
                this.$bvToast.toast('Error fetching notices', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                });
            }
        },

        async saveNotice() {
            if (!this.isFormValid) {
                this.$bvToast.toast('Please fill in title, subject and body', {
                    title: 'Validation Error',
                    variant: 'warning',
                    solid: true,
                });
                return;
            }

            try {
                this.loading = true;
                this.currentAction = 'save';
                
                let response;
                if (this.isEditing && this.editingId) {
                    response = await this.$lionheart.put(`/v2/admin/email-notices/${this.editingId}`, {
                        title: this.currentNotice.title.trim(),
                        subject: this.currentNotice.subject.trim(),
                        body: this.getBodyHtml()
                    });
                } else {
                    response = await this.$lionheart.post('/v2/admin/email-notices', {
                        title: this.currentNotice.title.trim(),
                        subject: this.currentNotice.subject.trim(),
                        body: this.getBodyHtml()
                    });
                }

                if (response.data.success) {
                    this.$bvToast.toast('Notice saved successfully!', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                    });
                    
                    // Update editingId if it was a new save
                    if (!this.isEditing) {
                        this.isEditing = true;
                        this.editingId = response.data.data.id;
                    }
                    
                    await this.fetchNoticesQuiet();
                } else {
                    throw new Error(response.data.message || 'Failed to save notice');
                }
            } catch (error) {
                console.error('Error saving notice:', error);
                this.$bvToast.toast(error.response?.data?.message || 'Error saving notice', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                });
            } finally {
                this.loading = false;
                this.currentAction = '';
            }
        },

        sendNotice() {
            if (!this.isFormValid) {
                this.$bvToast.toast('Please fill in title, subject and body', {
                    title: 'Validation Error',
                    variant: 'warning',
                    solid: true,
                });
                return;
            }

            // Must save first before sending
            if (!this.isEditing || !this.editingId) {
                this.$bvToast.toast('Please save the notice as a draft first before sending', {
                    title: 'Validation Error',
                    variant: 'warning',
                    solid: true,
                });
                return;
            }

            this.$bvModal.show('confirmation-modal');
        },

        async confirmSend() {
            try {
                this.loading = true;
                this.currentAction = 'send';
                this.$bvModal.hide('confirmation-modal');

                if (this.isEditing && this.editingId) {
                    await this.$lionheart.put(`/v2/admin/email-notices/${this.editingId}`, {
                        title: this.currentNotice.title.trim(),
                        subject: this.currentNotice.subject.trim(),
                        body: this.getBodyHtml()
                    });
                }
                
                const response = await this.$lionheart.post('/v2/admin/email-notices-sent', {
                    template_id: this.editingId
                });
                
                if (response?.data.success) {
                    this.$bvToast.toast('Emails sent successfully!', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 3000
                    });
                    
                    await this.fetchNoticesQuiet();
                    this.resetForm();
                    this.$bvModal.show('success-modal');
                } else {
                    throw new Error(response.data.message || 'Failed to send email');
                }
            
            } catch (error) {
                console.error('Error sending email:', error);
                
                this.$bvToast.toast(error.response?.data?.message || 'Error saving notice', 
                    {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        autoHideDelay: 3000
                    });
                    
                    setTimeout(async () => {
                        await this.fetchNoticesQuiet();
                    }, 2000);
                
                } finally {
                    this.loading = false;
                    this.currentAction = '';
                }
            },

        loadNotice(notice) {
            this.currentNotice = {
                title: notice.title || '',
                subject: notice.subject || '',
                body: notice.body || ''
            };
            this.isEditing = true;
            this.editingId = notice.id;
        },

        async deleteNotice(noticeId) {
            this.noticeToDelete = this.savedNotices.find(notice => notice.id === noticeId);
            this.$bvModal.show('delete-confirmation-modal');
        },

        async confirmDelete() {
            if (!this.noticeToDelete) return;

            try {
                this.loading = true;
                this.currentAction = 'delete';
                
                const response = await this.$lionheart.delete(`/v2/admin/email-notices/${this.noticeToDelete.id}`);
                
                if (response.data.success) {
                    this.$bvToast.toast('Notice deleted successfully!', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                    });
                    
                    if (this.isEditing && this.editingId === this.noticeToDelete.id) {
                        this.resetForm();
                    }
                    
                    this.$bvModal.hide('delete-confirmation-modal');
                    await this.fetchNoticesQuiet();
                } else {
                    throw new Error(response.data.message || 'Failed to delete notice');
                }
            } catch (error) {
                console.error('Error deleting notice:', error);
                this.$bvToast.toast(error.response?.data?.message || 'Error deleting notice', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                });
            } finally {
                this.loading = false;
                this.currentAction = '';
                this.noticeToDelete = null;
            }
        },

        async fetchNoticesQuiet() {
            try {
                const response = await this.$lionheart.get('/v2/admin/email-notices');
                
                if (response.data.success) {
                    this.savedNotices = response.data.data.drafts;
                    this.emailHistory = response.data.data.history;
                }
            } catch (error) {
                console.error('Error fetching notices:', error);
            }
        },

        async viewEmailContent(email) {
            try {
                const response = await this.$lionheart.get(`/v2/admin/email-notices/${email.id}`);
                
                if (response.data.success) {
                    this.selectedEmail = response.data.data;
                    this.$bvModal.show('email-content-modal');
                } else {
                    throw new Error('Failed to fetch email details');
                }
            } catch (error) {
                console.error('Error fetching email details:', error);
                this.$bvToast.toast('Error fetching email details', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                });
            }
        },

        resetForm() {
            this.currentNotice = {
                title: '',
                subject: '',
                body: ''
            };
            this.isEditing = false;
            this.editingId = null;
            this.currentAction = '';
        },

        truncateText(text, length) {
            const plainText = this.stripHtml(text);
            return plainText.length > length ? plainText.substring(0, length) + '...' : plainText;
        },

        stripHtml(text) {
            if (!text) return '';

            if (typeof window !== 'undefined' && window.DOMParser) {
                const parser = new window.DOMParser();
                const doc = parser.parseFromString(`<div>${text}</div>`, 'text/html');
                return (doc.body.textContent || '')
                    .replace(/\u00a0/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
            }

            return String(text)
                .replace(/<[^>]*>/g, ' ')
                .replace(/&nbsp;/gi, ' ')
                .replace(/&[a-zA-Z0-9#]+;/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
        },

        getBodyHtml() {
            return typeof this.currentNotice.body === 'string' ? this.currentNotice.body : '';
        },

        formatDateTime(dateTime) {
            if (!dateTime) return '';
            return new Date(dateTime).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
}